import React, { useState, useEffect } from 'react';
import Header from 'components/ui/Header';
import ContactQuickAccess from 'components/ui/ContactQuickAccess';
import Breadcrumb from 'components/ui/Breadcrumb';
import Icon from 'components/AppIcon';

import FeaturedArticles from './components/FeaturedArticles';
import ArticleGrid from './components/ArticleGrid';
import CategoryFilter from './components/CategoryFilter';
import SearchBar from './components/SearchBar';
import NewsletterSignup from './components/NewsletterSignup';
import PopularArticles from './components/PopularArticles';

const BlogResources = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredArticles, setFilteredArticles] = useState([]);

  const blogArticles = [
    {
      id: 1,
      title: "Mantenimiento Semanal de Piscinas en Costa Rica",
      titleEn: "Weekly Pool Maintenance in Costa Rica",
      excerpt: "Guía completa para mantener tu piscina en perfecto estado durante todo el año en el clima tropical costarricense.",
      excerptEn: "Complete guide to keeping your pool in perfect condition year-round in Costa Rica\'s tropical climate.",
      content: `El mantenimiento semanal de piscinas en Costa Rica requiere atención especial debido al clima tropical húmedo que favorece el crecimiento de algas y bacterias.

Durante la temporada lluviosa (mayo-noviembre), es crucial aumentar la frecuencia de limpieza y monitoreo químico. Las lluvias intensas pueden alterar el pH y diluir los químicos, creando condiciones ideales para el crecimiento de microorganismos.

Pasos esenciales del mantenimiento semanal:
1. Limpieza de superficie y skimmers
2. Aspirado del fondo de la piscina
3. Cepillado de paredes y escalones
4. Verificación y ajuste de niveles químicos
5. Limpieza del sistema de filtración
6. Inspección de equipos y accesorios

El clima costarricense presenta desafíos únicos como la alta humedad, temperaturas constantes de 24-30°C, y la presencia de ceniza volcánica que puede afectar la calidad del agua.`,
      image: "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=800&h=600&fit=crop",
      category: "maintenance",
      author: "Carlos Rodríguez",
      publishDate: "2024-01-15",
      readTime: "8 min",
      featured: true,
      tags: ["mantenimiento", "clima tropical", "limpieza semanal"]
    },
    {
      id: 2,
      title: "Balance Químico Perfecto para Piscinas Tropicales",
      titleEn: "Perfect Chemical Balance for Tropical Pools",
      excerpt: "Aprende a mantener el equilibrio químico ideal en tu piscina considerando las condiciones climáticas de Costa Rica.",
      excerptEn: "Learn to maintain the ideal chemical balance in your pool considering Costa Rica\'s climatic conditions.",
      content: `El balance químico en piscinas ubicadas en zonas tropicales como Costa Rica requiere un enfoque especializado debido a las altas temperaturas y humedad constante.

Los parámetros ideales para piscinas en Costa Rica son:
- pH: 7.2 - 7.6 (ligeramente más bajo debido al calor)
- Cloro libre: 1.0 - 3.0 ppm
- Alcalinidad total: 80 - 120 ppm
- Dureza cálcica: 150 - 300 ppm

El clima tropical acelera el consumo de cloro y puede causar fluctuaciones rápidas en el pH. La lluvia ácida común en algunas regiones de Costa Rica puede reducir significativamente el pH del agua.

Factores que afectan el balance químico:
- Temperatura del agua constantemente alta
- Lluvia frecuente durante 6 meses del año
- Alta radiación UV que degrada el cloro
- Presencia de materia orgánica del entorno tropical
- Ceniza volcánica ocasional`,
      image: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800&h=600&fit=crop",
      category: "chemicals",
      author: "María González",
      publishDate: "2024-01-12",
      readTime: "6 min",
      featured: true,
      tags: ["químicos", "pH", "cloro", "balance"]
    },
    {
      id: 3,
      title: "Preparación de Piscinas para la Temporada Lluviosa",
      titleEn: "Pool Preparation for Rainy Season",
      excerpt: "Consejos esenciales para preparar tu piscina antes de que lleguen las lluvias intensas de Costa Rica.",
      excerptEn: "Essential tips to prepare your pool before Costa Rica\'s intense rains arrive.",
      content: `La temporada lluviosa en Costa Rica (mayo a noviembre) presenta desafíos únicos para el mantenimiento de piscinas. La preparación adecuada es crucial para evitar problemas costosos.

Preparativos esenciales antes de la temporada lluviosa:

1. Inspección completa del sistema de drenaje
2. Limpieza profunda de filtros y skimmers
3. Verificación del sistema de bombeo
4. Ajuste preventivo de químicos
5. Instalación de cubiertas protectoras
6. Revisión de sistemas de iluminación subacuática

Durante los meses lluviosos, el agua de lluvia puede:
- Diluir los químicos hasta en un 40%
- Introducir contaminantes orgánicos
- Alterar el pH dramáticamente
- Sobrecargar el sistema de filtración
- Crear condiciones ideales para algas

La lluvia en Costa Rica puede ser intensa, con precipitaciones de hasta 300mm en un solo día en algunas regiones. Esto requiere un monitoreo más frecuente y ajustes químicos inmediatos después de lluvias fuertes.`,
      image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=600&fit=crop",
      category: "seasonal",
      author: "Roberto Jiménez",
      publishDate: "2024-01-10",
      readTime: "7 min",
      featured: false,
      tags: ["temporada lluviosa", "preparación", "prevención"]
    },
    {
      id: 4,
      title: "Equipos de Filtración Ideales para el Clima Costarricense",
      titleEn: "Ideal Filtration Equipment for Costa Rican Climate",
      excerpt: "Descubre qué sistemas de filtración funcionan mejor en las condiciones tropicales de Costa Rica.",
      excerptEn: "Discover which filtration systems work best in Costa Rica\'s tropical conditions.",
      content: `La selección del equipo de filtración adecuado es crucial para mantener piscinas en Costa Rica, donde el clima tropical presenta desafíos únicos para la calidad del agua.

Tipos de filtración recomendados para Costa Rica:

1. Filtros de arena de sílice
- Excelente para capturar partículas finas
- Resistente a la humedad tropical
- Fácil mantenimiento en clima húmedo
- Vida útil de 3-5 años en condiciones tropicales

2. Filtros de cartucho
- Ideales para piscinas residenciales pequeñas
- Fácil limpieza durante temporada lluviosa
- Captura partículas hasta 10 micrones
- Requiere reemplazo cada 2-4 meses

3. Filtros de tierra de diatomeas (DE)
- Filtración más fina disponible (2-5 micrones)
- Excelente para agua cristalina
- Requiere mantenimiento especializado
- Ideal para piscinas de alta gama

El clima costarricense acelera el crecimiento de biofilm en los sistemas de filtración, requiriendo limpieza más frecuente y uso de productos especializados para prevenir obstrucciones.`,
      image: "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=800&h=600&fit=crop",
      category: "equipment",
      author: "Ana Vargas",
      publishDate: "2024-01-08",
      readTime: "9 min",
      featured: false,
      tags: ["filtración", "equipos", "sistemas"]
    },
    {
      id: 5,
      title: "Control de Algas en Piscinas Tropicales",
      titleEn: "Algae Control in Tropical Pools",
      excerpt: "Estrategias efectivas para prevenir y eliminar algas en el ambiente húmedo de Costa Rica.",
      excerptEn: "Effective strategies to prevent and eliminate algae in Costa Rica\'s humid environment.",
      content: `El control de algas en Costa Rica requiere un enfoque proactivo debido a las condiciones ideales que ofrece el clima tropical para su crecimiento: altas temperaturas, humedad constante y abundante luz solar.

Tipos de algas comunes en Costa Rica:

1. Algas verdes (Chlorophyta)
- Las más comunes en piscinas tropicales
- Crecimiento rápido en agua tibia
- Fáciles de tratar si se detectan temprano

2. Algas amarillas/mostaza
- Resistentes al cloro estándar
- Comunes en áreas sombreadas
- Requieren tratamiento especializado

3. Algas negras
- Las más difíciles de eliminar
- Se adhieren fuertemente a superficies
- Requieren cepillado vigoroso y químicos específicos

Prevención efectiva:
- Mantener cloro libre entre 1-3 ppm constantemente
- Circulación del agua 8-12 horas diarias
- Cepillado semanal de todas las superficies
- Limpieza regular de línea de flotación
- Uso de algicidas preventivos durante temporada lluviosa

El clima de Costa Rica puede duplicar la velocidad de crecimiento de algas comparado con climas templados, haciendo la prevención aún más crítica.`,
      image: "https://images.unsplash.com/photo-1566041510394-cf7c8fe21800?w=800&h=600&fit=crop",
      category: "maintenance",
      author: "Luis Fernández",
      publishDate: "2024-01-05",
      readTime: "6 min",
      featured: false,
      tags: ["algas", "prevención", "tratamiento"]
    },
    {
      id: 6,
      title: "Ahorro de Energía en Sistemas de Piscina",
      titleEn: "Energy Saving in Pool Systems",
      excerpt: "Consejos para reducir el consumo energético de tu piscina sin comprometer la calidad del agua.",
      excerptEn: "Tips to reduce your pool\'s energy consumption without compromising water quality.",
      content: `En Costa Rica, donde la electricidad puede ser costosa, optimizar el consumo energético de tu piscina es esencial para mantener costos operativos razonables.

Estrategias de ahorro energético:

1. Bombas de velocidad variable
- Reducen consumo hasta 70%
- Ajustan velocidad según necesidades
- Amortización en 2-3 años
- Ideales para clima tropical constante

2. Calentadores solares
- Aprovechan abundante sol costarricense
- Reducen costos de calentamiento 80%
- Mantenimiento mínimo
- Vida útil de 15-20 años

3. Cubiertas térmicas
- Reducen evaporación hasta 95%
- Mantienen temperatura nocturna
- Previenen contaminación por lluvia
- Reducen uso de químicos 40%

4. Iluminación LED
- Consumen 75% menos energía
- Vida útil de 25,000 horas
- Resistentes a humedad tropical
- Variedad de colores disponibles

5. Temporizadores inteligentes
- Optimizan horarios de operación
- Aprovechan tarifas eléctricas nocturnas
- Control remoto via smartphone
- Programación estacional automática

El clima estable de Costa Rica permite programaciones más eficientes que en países con estaciones marcadas.`,
      image: "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=800&h=600&fit=crop",
      category: "equipment",
      author: "Patricia Morales",
      publishDate: "2024-01-03",
      readTime: "8 min",
      featured: false,
      tags: ["ahorro energético", "eficiencia", "costos"]
    }
  ];

  const categories = [
    { id: 'all', label: 'Todos los Artículos', labelEn: 'All Articles', icon: 'BookOpen' },
    { id: 'maintenance', label: 'Mantenimiento', labelEn: 'Maintenance', icon: 'Wrench' },
    { id: 'chemicals', label: 'Balance Químico', labelEn: 'Chemical Balance', icon: 'Beaker' },
    { id: 'equipment', label: 'Equipos', labelEn: 'Equipment', icon: 'Settings' },
    { id: 'seasonal', label: 'Consejos Estacionales', labelEn: 'Seasonal Tips', icon: 'Calendar' }
  ];

  useEffect(() => {
    let filtered = blogArticles;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(article => article.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    setFilteredArticles(filtered);
  }, [selectedCategory, searchQuery]);

  const featuredArticles = blogArticles.filter(article => article.featured);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ContactQuickAccess />
      
      <main className="pt-16">
        {/* Hero Section */}
        <section className="bg-primary text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6">
                Blog & Recursos
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">
                Consejos expertos, guías detalladas y recursos para el mantenimiento perfecto de tu piscina en Costa Rica
              </p>
              <div className="flex items-center justify-center space-x-4 text-blue-100">
                <div className="flex items-center space-x-2">
                  <Icon name="BookOpen" size={20} />
                  <span>Guías Especializadas</span>
                </div>
                <div className="hidden sm:block w-px h-6 bg-blue-300"></div>
                <div className="flex items-center space-x-2">
                  <Icon name="Users" size={20} />
                  <span>Consejos de Expertos</span>
                </div>
                <div className="hidden sm:block w-px h-6 bg-blue-300"></div>
                <div className="flex items-center space-x-2">
                  <Icon name="Zap" size={20} />
                  <span>Actualizaciones Semanales</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb />

          {/* Search and Filter Section */}
          <div className="mb-12">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <SearchBar 
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
              />
              <CategoryFilter
                categories={categories}
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
              />
            </div>
          </div>

          {/* Featured Articles */}
          {selectedCategory === 'all' && !searchQuery && (
            <FeaturedArticles articles={featuredArticles} />
          )}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Articles Grid */}
            <div className="lg:col-span-3">
              <div className="mb-6">
                <h2 className="text-2xl font-heading font-semibold text-text-primary mb-2">
                  {selectedCategory === 'all' ? 'Todos los Artículos' : 
                   categories.find(cat => cat.id === selectedCategory)?.label}
                </h2>
                <p className="text-text-secondary">
                  {filteredArticles.length} artículo{filteredArticles.length !== 1 ? 's' : ''} encontrado{filteredArticles.length !== 1 ? 's' : ''}
                </p>
              </div>
              
              <ArticleGrid articles={filteredArticles} />
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="space-y-8">
                {/* Popular Articles */}
                <PopularArticles articles={blogArticles.slice(0, 5)} />

                {/* Newsletter Signup */}
                <NewsletterSignup />

                {/* Quick Tips */}
                <div className="bg-surface rounded-lg border border-border p-6">
                  <h3 className="text-lg font-heading font-semibold text-text-primary mb-4 flex items-center">
                    <Icon name="Lightbulb" size={20} className="text-accent mr-2" />
                    Consejos Rápidos
                  </h3>
                  <div className="space-y-4">
                    <div className="p-3 bg-primary/5 rounded-lg">
                      <p className="text-sm text-text-primary font-medium mb-1">
                        Temporada Lluviosa
                      </p>
                      <p className="text-xs text-text-secondary">
                        Aumenta la frecuencia de pruebas químicas durante mayo-noviembre
                      </p>
                    </div>
                    <div className="p-3 bg-success/5 rounded-lg">
                      <p className="text-sm text-text-primary font-medium mb-1">
                        Ahorro de Cloro
                      </p>
                      <p className="text-xs text-text-secondary">
                        Usa estabilizador para proteger el cloro del sol tropical
                      </p>
                    </div>
                    <div className="p-3 bg-warning/5 rounded-lg">
                      <p className="text-sm text-text-primary font-medium mb-1">
                        Emergencia
                      </p>
                      <p className="text-xs text-text-secondary">
                        Contacta profesionales si el agua se torna verde en 24h
                      </p>
                    </div>
                  </div>
                </div>

                {/* Contact CTA */}
                <div className="bg-gradient-to-br from-primary to-secondary text-white rounded-lg p-6">
                  <h3 className="text-lg font-heading font-semibold mb-2">
                    ¿Necesitas Ayuda Profesional?
                  </h3>
                  <p className="text-blue-100 text-sm mb-4">
                    Nuestros expertos están listos para resolver cualquier problema de tu piscina
                  </p>
                  <button className="w-full bg-white text-primary px-4 py-2 rounded-lg font-medium hover:bg-blue-50 nav-transition">
                    Solicitar Consulta
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-text-primary text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Waves" size={24} color="white" />
                </div>
                <div>
                  <h3 className="text-xl font-heading font-bold">BAC Pool CR</h3>
                  <p className="text-sm text-gray-300">Servicios Profesionales de Piscinas</p>
                </div>
              </div>
              <p className="text-gray-300 mb-4">
                Expertos en mantenimiento y limpieza de piscinas en Costa Rica. 
                Servicios profesionales con más de 10 años de experiencia.
              </p>
              <div className="flex space-x-4">
                <Icon name="Facebook" size={20} className="text-gray-400 hover:text-white cursor-pointer nav-transition" />
                <Icon name="Instagram" size={20} className="text-gray-400 hover:text-white cursor-pointer nav-transition" />
                <Icon name="Mail" size={20} className="text-gray-400 hover:text-white cursor-pointer nav-transition" />
              </div>
            </div>
            
            <div>
              <h4 className="font-heading font-semibold mb-4">Contacto</h4>
              <div className="space-y-2 text-sm text-gray-300">
                <div className="flex items-center space-x-2">
                  <Icon name="Phone" size={16} />
                  <span>+506 8888-8888</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Mail" size={16} />
                  <span>info@bacpoolcr.com</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} />
                  <span>San José, Costa Rica</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-heading font-semibold mb-4">Horarios</h4>
              <div className="space-y-2 text-sm text-gray-300">
                <div>Lunes - Viernes: 7AM - 6PM</div>
                <div>Sábados: 8AM - 4PM</div>
                <div>Domingos: Emergencias</div>
                <div className="text-accent font-medium">Disponible 24/7</div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} BAC Pool CR. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogResources;